import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import { MotionConfig } from 'framer-motion'
import { getUserPreference, setUserPreference } from '../services/userPreferencesService'
import { updateStatusBarForTheme } from '../hooks/useNativeInit'
import { useAuth } from './AuthContext'

const PREFERENCE_KEY = 'theme_preference'
const STORAGE_KEY = 'teesync-appearance'
const VALID_VALUES = ['system', 'light', 'dark']

const REDUCE_MOTION_PREFERENCE_KEY = 'reduce_motion'
const REDUCE_MOTION_STORAGE_KEY = 'teesync-reduce-motion'

const AppearanceContext = createContext({})

export const useAppearance = () => {
  const context = useContext(AppearanceContext)
  if (!context) {
    throw new Error('useAppearance must be used within AppearanceProvider')
  }
  return context
}

const readSystemPreference = () =>
  window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

const resolveTheme = (appearance) =>
  appearance === 'system' ? readSystemPreference() : appearance

const applyTheme = (resolved) => {
  if (resolved === 'dark') {
    document.documentElement.setAttribute('data-theme', 'dark')
  } else {
    document.documentElement.removeAttribute('data-theme')
  }
  updateStatusBarForTheme(resolved === 'dark')
}

export const AppearanceProvider = ({ children }) => {
  const { user } = useAuth()

  const [appearance, setAppearanceState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      return VALID_VALUES.includes(stored) ? stored : 'system'
    } catch {
      return 'system'
    }
  })

  const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme(appearance))
  const hasLoadedFromServerRef = useRef(false)

  const [reduceMotion, setReduceMotionState] = useState(() => {
    try {
      return localStorage.getItem(REDUCE_MOTION_STORAGE_KEY) === 'true'
    } catch {
      return false
    }
  })
  const hasLoadedMotionFromServerRef = useRef(false)

  useEffect(() => {
    if (reduceMotion) {
      document.body.classList.add('reduce-motion')
    } else {
      document.body.classList.remove('reduce-motion')
    }
  }, [reduceMotion])

  useEffect(() => {
    const resolved = resolveTheme(appearance)
    setResolvedTheme(resolved)
    applyTheme(resolved)
  }, [appearance])

  useEffect(() => {
    if (appearance !== 'system') return

    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => {
      const resolved = resolveTheme('system')
      setResolvedTheme(resolved)
      applyTheme(resolved)
    }
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [appearance])

  useEffect(() => {
    if (!user || hasLoadedFromServerRef.current) return
    hasLoadedFromServerRef.current = true

    let cancelled = false
    ;(async () => {
      const { data } = await getUserPreference(PREFERENCE_KEY, null, user)
      if (cancelled) return
      if (VALID_VALUES.includes(data) && data !== appearance) {
        setAppearanceState(data)
        try {
          localStorage.setItem(STORAGE_KEY, data)
        } catch {}
      }
    })()
    return () => {
      cancelled = true
    }
  }, [user, appearance])

  const setAppearance = useCallback(
    async (value) => {
      if (!VALID_VALUES.includes(value)) return
      setAppearanceState(value)
      try {
        localStorage.setItem(STORAGE_KEY, value)
      } catch {}
      if (user) {
        await setUserPreference(PREFERENCE_KEY, value, user)
      }
    },
    [user]
  )

  useEffect(() => {
    if (!user || hasLoadedMotionFromServerRef.current) return
    hasLoadedMotionFromServerRef.current = true

    let cancelled = false
    ;(async () => {
      const { data } = await getUserPreference(REDUCE_MOTION_PREFERENCE_KEY, null, user)
      if (cancelled || data == null) return
      const next = data === 'true' || data === true
      setReduceMotionState(next)
      try {
        localStorage.setItem(REDUCE_MOTION_STORAGE_KEY, next ? 'true' : 'false')
      } catch {}
    })()
    return () => {
      cancelled = true
    }
  }, [user])

  const setReduceMotion = useCallback(
    async (enabled) => {
      const next = !!enabled
      setReduceMotionState(next)
      try {
        localStorage.setItem(REDUCE_MOTION_STORAGE_KEY, next ? 'true' : 'false')
      } catch {}
      if (user) {
        await setUserPreference(REDUCE_MOTION_PREFERENCE_KEY, next ? 'true' : 'false', user)
      }
    },
    [user]
  )

  return (
    <AppearanceContext.Provider
      value={{ appearance, resolvedTheme, setAppearance, reduceMotion, setReduceMotion }}
    >
      <MotionConfig reducedMotion={reduceMotion ? 'always' : 'user'}>
        {children}
      </MotionConfig>
    </AppearanceContext.Provider>
  )
}
